import { useEffect, useState, useCallback, useRef } from 'react';
import { apiJson } from '../lib/api.js';

const CARD = {
  background: '#fff',
  borderRadius: 16,
  padding: '18px 22px',
  border: '1px solid #ece8f6',
  boxShadow: '0 8px 22px rgba(20, 5, 60, 0.05)',
};
const SECTION_HEAD = { fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#5b21b6', marginBottom: 8 };
const PURPLE = '#7c3aed';

const RANGES = [
  { v: 'today', label: 'Today' },
  { v: '24h', label: '24 hours' },
  { v: '7d', label: '7 days' },
  { v: '28d', label: '28 days' },
  { v: '90d', label: '90 days' },
];

const METRICS = [
  { key: 'pageviews', label: 'Pageviews', color: '#7c3aed' },
  { key: 'visitors', label: 'Visitors', color: '#0891b2' },
  { key: 'sessions', label: 'Sessions', color: '#db2777' },
  { key: 'events', label: 'Events', color: '#16a34a' },
  { key: 'conversions', label: 'Conversions', color: '#ea580c' },
];

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const nf = new Intl.NumberFormat('en-GB');
const fmt = (n) => (n == null ? '—' : nf.format(Math.round(n)));

function fmtDuration(seconds) {
  const s = Math.round(seconds || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const fmtPct = (x) => `${((x || 0) * 100).toFixed(1)}%`;

function flagEmoji(cc) {
  if (!cc || cc.length !== 2 || !/^[A-Za-z]{2}$/.test(cc)) return '🌐';
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
}

function deltaInfo(curr, prev, goodWhenDown = false) {
  if (prev == null || prev === 0) {
    if (!curr) return { text: '—', color: '#9ca3af', arrow: '' };
    return { text: 'new', color: '#15803d', arrow: '▲' };
  }
  const change = (curr - prev) / prev;
  const up = change >= 0;
  const good = goodWhenDown ? !up : up;
  return {
    text: `${up ? '+' : ''}${(change * 100).toFixed(0)}%`,
    color: change === 0 ? '#9ca3af' : good ? '#15803d' : '#dc2626',
    arrow: change === 0 ? '' : up ? '▲' : '▼',
  };
}

function Sparkline({ series, dataKey, color = PURPLE }) {
  if (!series || series.length < 2) return null;
  const vals = series.map((d) => Number(d[dataKey]) || 0);
  const max = Math.max(1, ...vals);
  const W = 100;
  const H = 28;
  const step = W / (vals.length - 1);
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(H - (v / max) * (H - 3) - 1).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block', marginTop: 6 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.85" />
    </svg>
  );
}

function MetricCard({ label, value, delta, hint, accent = PURPLE, series, dataKey }) {
  return (
    <div style={{ ...CARD, padding: '14px 16px', borderLeft: `4px solid ${accent}` }}>
      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 23, fontWeight: 800, color: '#1f1240' }}>{value}</span>
        {delta && <span style={{ fontSize: 12, fontWeight: 700, color: delta.color }}>{delta.arrow} {delta.text}</span>}
      </div>
      {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{hint}</div>}
      {dataKey && <Sparkline series={series} dataKey={dataKey} color={accent} />}
    </div>
  );
}

function TrafficChart({ data, metric, interval }) {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  const W = 1000;
  const H = 280;
  const padX = 8;
  const padTop = 16;
  const padBottom = 28;
  const m = METRICS.find((x) => x.key === metric) || METRICS[0];

  if (!data || data.length === 0) {
    return <div style={{ color: '#9ca3af', textAlign: 'center', padding: 60 }}>No traffic in this period yet.</div>;
  }

  const values = data.map((d) => Number(d[metric]) || 0);
  const max = Math.max(1, ...values);
  const n = data.length;
  const stepX = n > 1 ? (W - padX * 2) / (n - 1) : 0;
  const yFor = (v) => padTop + (H - padTop - padBottom) * (1 - v / max);
  const xFor = (i) => padX + stepX * i;

  const linePts = data.map((d, i) => `${xFor(i)},${yFor(Number(d[metric]) || 0)}`).join(' ');
  const areaPts = `${padX},${H - padBottom} ${linePts} ${xFor(n - 1)},${H - padBottom}`;

  const labelFor = (bucket) => {
    const dt = new Date(bucket);
    if (interval === 'hour') return dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const ticks = [];
  const tickCount = Math.min(7, n);
  for (let t = 0; t < tickCount; t++) ticks.push(Math.round((t / Math.max(1, tickCount - 1)) * (n - 1)));

  const onMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ratio = (e.clientX - rect.left) / rect.width;
    setHover(Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1)))));
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="mm-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={m.color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={m.color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => (
          <line key={g} x1={padX} x2={W - padX} y1={padTop + (H - padTop - padBottom) * g} y2={padTop + (H - padTop - padBottom) * g} stroke="#f0ecf9" strokeWidth="1" />
        ))}
        <polygon points={areaPts} fill="url(#mm-area)" />
        <polyline points={linePts} fill="none" stroke={m.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {hover != null && (
          <>
            <line x1={xFor(hover)} x2={xFor(hover)} y1={padTop} y2={H - padBottom} stroke={m.color} strokeWidth="1" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
            <circle cx={xFor(hover)} cy={yFor(values[hover])} r="4" fill="#fff" stroke={m.color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
          </>
        )}
      </svg>
      <div style={{ position: 'relative', height: 16, marginTop: 2 }}>
        {ticks.map((i) => (
          <span key={i} style={{ position: 'absolute', left: `${(xFor(i) / W) * 100}%`, transform: 'translateX(-50%)', fontSize: 10.5, color: '#9ca3af', whiteSpace: 'nowrap' }}>
            {labelFor(data[i].bucket)}
          </span>
        ))}
      </div>
      {hover != null && (
        <div style={{ position: 'absolute', top: 0, left: `${(xFor(hover) / W) * 100}%`, transform: `translateX(${hover > n / 2 ? '-105%' : '5%'})`, background: '#1f1240', color: '#fff', borderRadius: 8, padding: '7px 10px', fontSize: 12, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 6px 18px rgba(0,0,0,0.25)' }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>{labelFor(data[hover].bucket)}</div>
          <div>Pageviews: <strong>{fmt(data[hover].pageviews)}</strong></div>
          <div>Visitors: <strong>{fmt(data[hover].visitors)}</strong></div>
          <div>Sessions: <strong>{fmt(data[hover].sessions)}</strong></div>
          <div>Conversions: <strong>{fmt(data[hover].conversions)}</strong></div>
        </div>
      )}
    </div>
  );
}

function BarList({ rows, primaryKey = 'visitors', secondaryKey = 'pageviews', primaryLabel = 'Visitors', secondaryLabel = 'Views', extra, renderKey, accent = PURPLE, empty = 'No data yet', filterKey, onFilter, activeValue }) {
  if (!rows || rows.length === 0) return <div style={{ color: '#9ca3af', fontSize: 13, padding: '14px 2px' }}>{empty}</div>;
  const max = Math.max(1, ...rows.map((r) => Number(r[primaryKey]) || 0));
  const clickable = Boolean(filterKey && onFilter);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', paddingBottom: 2 }}>
        <span>&nbsp;</span>
        <span style={{ display: 'flex', gap: 16 }}>
          {extra && <span style={{ width: 60, textAlign: 'right' }}>{extra.label}</span>}
          <span style={{ width: 52, textAlign: 'right' }}>{primaryLabel}</span>
          {secondaryKey && <span style={{ width: 46, textAlign: 'right' }}>{secondaryLabel}</span>}
        </span>
      </div>
      {rows.map((r, i) => {
        const active = activeValue != null && r.key === activeValue;
        return (
          <div
            key={(r.key ?? i) + ''}
            onClick={clickable ? () => onFilter(filterKey, r.key) : undefined}
            title={clickable ? `Filter by ${filterKey}: ${r.key}` : undefined}
            style={{
              position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '5px 8px', borderRadius: 7, overflow: 'hidden',
              cursor: clickable ? 'pointer' : 'default',
              outline: active ? `2px solid ${accent}` : 'none',
            }}
          >
            <div style={{ position: 'absolute', inset: 0, width: `${((Number(r[primaryKey]) || 0) / max) * 100}%`, background: `${accent}14`, borderRadius: 7 }} />
            <span style={{ position: 'relative', fontSize: 13, color: '#1f1240', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '52%', display: 'flex', alignItems: 'center', gap: 5 }}>
              {r.is_conversion && <span title="Conversion event" style={{ color: '#ea580c' }}>★</span>}
              {renderKey ? renderKey(r.key) : (r.key || '(not set)')}
            </span>
            <span style={{ position: 'relative', display: 'flex', gap: 16, fontVariantNumeric: 'tabular-nums', alignItems: 'center' }}>
              {extra && <span style={{ width: 60, textAlign: 'right', color: '#6b7280', fontSize: 12 }}>{extra.format(r[extra.key])}</span>}
              <span style={{ width: 52, textAlign: 'right', fontWeight: 700, color: '#1f1240', fontSize: 13 }}>{fmt(r[primaryKey])}</span>
              {secondaryKey && <span style={{ width: 46, textAlign: 'right', color: '#6b7280', fontSize: 12.5 }}>{fmt(r[secondaryKey])}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function BreakdownCard({ title, children, note }) {
  return (
    <div style={{ ...CARD }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={SECTION_HEAD}>{title}</div>
        {note && <span style={{ fontSize: 10.5, color: '#9ca3af' }}>{note}</span>}
      </div>
      {children}
    </div>
  );
}

function Funnel({ steps }) {
  if (!steps || steps.length === 0) return null;
  const top = Math.max(1, Number(steps[0].n) || 0);
  const colors = ['#7c3aed', '#0891b2', '#ea580c'];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {steps.map((s, i) => {
        const n = Number(s.n) || 0;
        const pctOfTop = (n / top) * 100;
        const prev = i > 0 ? Number(steps[i - 1].n) || 0 : null;
        const stepPct = prev ? (n / prev) * 100 : null;
        return (
          <div key={s.step}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 3 }}>
              <span style={{ fontWeight: 600, color: '#1f1240' }}>{s.step}</span>
              <span style={{ color: '#6b7280' }}>
                <strong style={{ color: '#1f1240' }}>{fmt(n)}</strong>
                {' '}({pctOfTop.toFixed(0)}%)
                {stepPct != null && <span style={{ color: stepPct >= 60 ? '#15803d' : stepPct >= 30 ? '#b45309' : '#dc2626', marginLeft: 6 }}>↓{(100 - stepPct).toFixed(0)}% drop</span>}
              </span>
            </div>
            <div style={{ height: 26, background: '#f3eefc', borderRadius: 7, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(2, pctOfTop)}%`, height: '100%', background: colors[i % colors.length], borderRadius: 7, transition: 'width 0.3s' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Heatmap({ cells }) {
  if (!cells || cells.length === 0) return <div style={{ color: '#9ca3af', fontSize: 13 }}>Not enough data yet.</div>;
  const grid = {};
  let max = 1;
  for (const c of cells) {
    grid[`${c.dow}-${c.hour}`] = c.pageviews;
    if (c.pageviews > max) max = c.pageviews;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `34px repeat(24, 1fr)`, gap: 2, minWidth: 560 }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} style={{ fontSize: 8.5, color: '#9ca3af', textAlign: 'center' }}>{h % 3 === 0 ? h : ''}</div>
        ))}
        {DOW_LABELS.map((lbl, d) => (
          <Row key={lbl} label={lbl} dow={d + 1} grid={grid} max={max} />
        ))}
      </div>
      <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 8 }}>Pageviews by hour (UTC) × weekday. Darker = busier.</div>
    </div>
  );
}
function Row({ label, dow, grid, max }) {
  return (
    <>
      <div style={{ fontSize: 10.5, color: '#6b7280', fontWeight: 600, display: 'flex', alignItems: 'center' }}>{label}</div>
      {Array.from({ length: 24 }, (_, h) => {
        const v = grid[`${dow}-${h}`] || 0;
        const a = v === 0 ? 0 : 0.12 + (v / max) * 0.88;
        return <div key={h} title={`${label} ${h}:00 — ${v} pageviews`} style={{ aspectRatio: '1 / 1', borderRadius: 2, background: v === 0 ? '#f4f1fb' : `rgba(124,58,237,${a.toFixed(2)})` }} />;
      })}
    </>
  );
}

function Cohorts({ rows }) {
  if (!rows || rows.length === 0) return <div style={{ color: '#9ca3af', fontSize: 13 }}>Not enough history yet — cohorts build up week over week.</div>;
  const byCohort = new Map();
  let maxOffset = 0;
  for (const r of rows) {
    if (!byCohort.has(r.cohort)) byCohort.set(r.cohort, {});
    byCohort.get(r.cohort)[r.week_offset] = r.visitors;
    if (r.week_offset > maxOffset) maxOffset = r.week_offset;
  }
  const cohortKeys = [...byCohort.keys()].sort();
  const offsets = Array.from({ length: maxOffset + 1 }, (_, i) => i);
  const cellColor = (pct) => {
    if (pct == null) return '#f8f7fc';
    const a = 0.15 + (pct / 100) * 0.85;
    return `rgba(22,163,74,${a.toFixed(2)})`;
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 3, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', color: '#9ca3af', fontWeight: 700, fontSize: 10.5, textTransform: 'uppercase', padding: '0 6px' }}>Cohort</th>
            <th style={{ color: '#9ca3af', fontWeight: 700, fontSize: 10.5, padding: '0 6px' }}>Size</th>
            {offsets.map((o) => <th key={o} style={{ color: '#9ca3af', fontWeight: 700, fontSize: 10.5, padding: '0 4px' }}>W{o}</th>)}
          </tr>
        </thead>
        <tbody>
          {cohortKeys.map((c) => {
            const data = byCohort.get(c);
            const size = data[0] || 0;
            return (
              <tr key={c}>
                <td style={{ color: '#1f1240', fontWeight: 600, whiteSpace: 'nowrap', paddingRight: 6 }}>{new Date(c).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                <td style={{ textAlign: 'center', color: '#6b7280', fontWeight: 700 }}>{fmt(size)}</td>
                {offsets.map((o) => {
                  const v = data[o];
                  const pct = v != null && size ? (v / size) * 100 : null;
                  return (
                    <td key={o} title={v != null ? `${v} returned (${pct ? pct.toFixed(0) : 0}%)` : ''} style={{ width: 42, height: 26, textAlign: 'center', borderRadius: 4, background: cellColor(pct), color: pct != null && pct > 55 ? '#fff' : '#1f1240', fontWeight: 600 }}>
                      {pct != null ? `${pct.toFixed(0)}%` : ''}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 8 }}>% of each weekly cohort of new visitors that returned in later weeks.</div>
    </div>
  );
}

function FilterChips({ filters, onRemove, onClear }) {
  const entries = Object.entries(filters).filter(([, v]) => v);
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6' }}>Segment:</span>
      {entries.map(([k, v]) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3eefc', border: '1px solid #d8c9f5', color: '#5b21b6', borderRadius: 999, padding: '4px 6px 4px 12px', fontSize: 12.5, fontWeight: 600 }}>
          {k}: {v}
          <button type="button" onClick={() => onRemove(k)} style={{ border: 'none', background: '#e5d8fb', color: '#5b21b6', borderRadius: 999, width: 18, height: 18, cursor: 'pointer', lineHeight: 1, fontSize: 13 }}>×</button>
        </span>
      ))}
      <button type="button" onClick={onClear} style={{ border: '1px solid #d8d6e6', background: '#fff', color: '#4b4361', borderRadius: 999, padding: '4px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Clear all</button>
    </div>
  );
}

function RealtimePanel({ rt }) {
  const max = Math.max(1, ...(rt?.per_minute || []).map((p) => Number(p.pageviews) || 0));
  return (
    <div style={{ ...CARD, background: 'linear-gradient(135deg, #faf7ff, #fff)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={SECTION_HEAD}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: '#22c55e', marginRight: 6, animation: 'mm-pulse-dot 1.6s infinite' }} />
          Realtime · last 30 min
        </div>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>auto-refresh 15s</span>
      </div>
      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-end', marginTop: 6, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 34, fontWeight: 800, color: '#5b21b6', lineHeight: 1 }}>{fmt(rt?.active_visitors)}</div>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>active visitors</div>
        </div>
        <div>
          <div style={{ fontSize: 34, fontWeight: 800, color: '#1f1240', lineHeight: 1 }}>{fmt(rt?.pageviews)}</div>
          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>pageviews</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 44, marginBottom: 14 }}>
        {(rt?.per_minute || []).map((p, i) => (
          <div key={i} title={`${fmt(p.pageviews)} pageviews`} style={{ flex: 1, height: `${Math.max(4, ((Number(p.pageviews) || 0) / max) * 100)}%`, background: i === (rt.per_minute.length - 1) ? '#22c55e' : '#c4b5fd', borderRadius: 2, minHeight: 3 }} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Top pages now</div>
          {(rt?.pages || []).length === 0 ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nobody on site right now.</div> : (rt.pages || []).map((p, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '2px 0', color: '#3f3a5e' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{p.key || '/'}</span>
              <strong style={{ color: '#1f1240' }}>{fmt(p.visitors)}</strong>
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>Live activity</div>
          <div style={{ maxHeight: 132, overflowY: 'auto' }}>
            {(rt?.recent || []).length === 0 ? <div style={{ fontSize: 12.5, color: '#9ca3af' }}>—</div> : (rt.recent || []).map((e, i) => (
              <div key={i} style={{ fontSize: 12, padding: '2px 0', color: '#3f3a5e', display: 'flex', gap: 6, alignItems: 'center' }}>
                <span>{flagEmoji(e.country)}</span>
                {e.event_type === 'event'
                  ? <span><strong style={{ color: '#7c3aed' }}>{e.name}</strong> · {e.path || '/'}</span>
                  : <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.path || '/'}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function downloadCsv(data, range) {
  const esc = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [`Medic Mind analytics export — ${range} — ${new Date().toISOString()}`, '', 'Metric,Value'];
  const s = data.summary || {};
  [
    ['Pageviews', s.pageviews], ['Unique visitors', s.visitors], ['New visitors', s.new_visitors],
    ['Sessions', s.sessions], ['Engaged sessions', s.engaged_sessions],
    ['Avg session (s)', Math.round(s.avg_session_seconds)], ['Avg engagement (s)', Math.round(s.avg_engagement_seconds)],
    ['Bounce rate', fmtPct(s.bounce_rate)], ['Pages / session', (s.pages_per_session || 0).toFixed(2)],
    ['Conversions', s.conversions], ['Converting sessions', s.converting_sessions], ['Signups', s.signups],
  ].forEach(([k, v]) => lines.push(`${esc(k)},${esc(v)}`));

  const sections = {
    'Top pages': data.breakdowns.pages, Channels: data.breakdowns.channels, Sources: data.breakdowns.sources,
    Referrers: data.breakdowns.referrers, 'Landing pages': data.breakdowns.landing, 'Exit pages': data.breakdowns.exit,
    Devices: data.breakdowns.devices, Browsers: data.breakdowns.browsers, 'Operating systems': data.breakdowns.os,
    Countries: data.breakdowns.countries, Languages: data.breakdowns.languages, Events: data.events,
  };
  for (const [name, rows] of Object.entries(sections)) {
    lines.push('', name, 'Key,Primary,Visitors');
    (rows || []).forEach((r) => lines.push([esc(r.key), esc(r.pageviews ?? r.count ?? r.sessions ?? ''), esc(r.visitors ?? '')].join(',')));
  }

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `medic-mind-analytics-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AnalyticsDashboard() {
  const [range, setRange] = useState('7d');
  const [metric, setMetric] = useState('pageviews');
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [rt, setRt] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const queryString = useCallback((r, f) => {
    const p = new URLSearchParams({ range: r });
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, []);

  const fetchAll = useCallback(async (r, f) => {
    setLoading(true);
    setErr('');
    const qs = queryString(r, f);
    try {
      const [ov, ins] = await Promise.all([
        apiJson(`/api/analytics/overview?${qs}`),
        apiJson(`/api/analytics/insights?${qs}`).catch(() => null),
      ]);
      setData(ov);
      setInsights(ins);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  const fetchRealtime = useCallback(async () => {
    try { setRt(await apiJson('/api/analytics/realtime')); } catch { /* keep last */ }
  }, []);

  useEffect(() => { fetchAll(range, filters); }, [range, filters, fetchAll]);
  useEffect(() => {
    fetchRealtime();
    const id = setInterval(fetchRealtime, 15000);
    return () => clearInterval(id);
  }, [fetchRealtime]);

  const applyFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (next[key] === value) delete next[key];
      else next[key] = value;
      return next;
    });
  };
  const removeFilter = (key) => setFilters((prev) => { const n = { ...prev }; delete n[key]; return n; });

  const s = data?.summary;
  const p = data?.previous;
  const ts = data?.timeseries;
  const convRate = s && s.sessions ? s.converting_sessions / s.sessions : 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <p style={{ color: '#4b4361', fontSize: 14, margin: 0, maxWidth: 560 }}>
          First-party traffic analytics — no sampling. Click any row to segment the whole report. Compared to the previous {data?.range || range} period.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => data && downloadCsv(data, range)} disabled={!data} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #d8d6e6', background: '#fff', color: '#3f3a5e', fontWeight: 600, fontSize: 13, cursor: data ? 'pointer' : 'default' }}>⬇ Export CSV</button>
          <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid #ece8f6', borderRadius: 10, padding: 3 }}>
            {RANGES.map((r) => (
              <button key={r.v} type="button" onClick={() => setRange(r.v)} style={{
                padding: '6px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: range === r.v ? '#5b21b6' : 'transparent', color: range === r.v ? '#fff' : '#4b4361',
              }}>{r.label}</button>
            ))}
          </div>
        </div>
      </div>

      <FilterChips filters={filters} onRemove={removeFilter} onClear={() => setFilters({})} />

      {err && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginBottom: 12 }}>{err}</div>}

      <div style={{ marginBottom: 16 }}>
        <RealtimePanel rt={rt} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 16, opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s' }}>
        <MetricCard label="Pageviews" value={fmt(s?.pageviews)} delta={deltaInfo(s?.pageviews, p?.pageviews)} accent="#7c3aed" series={ts} dataKey="pageviews" />
        <MetricCard label="Unique visitors" value={fmt(s?.visitors)} delta={deltaInfo(s?.visitors, p?.visitors)} hint={`${fmt(s?.new_visitors)} new · ${fmt((s?.visitors || 0) - (s?.new_visitors || 0))} returning`} accent="#0891b2" series={ts} dataKey="visitors" />
        <MetricCard label="Sessions" value={fmt(s?.sessions)} delta={deltaInfo(s?.sessions, p?.sessions)} accent="#db2777" series={ts} dataKey="sessions" />
        <MetricCard label="Conversions" value={fmt(s?.conversions)} delta={deltaInfo(s?.conversions, p?.conversions)} hint={`${fmtPct(convRate)} of sessions`} accent="#ea580c" series={ts} dataKey="conversions" />
        <MetricCard label="Avg. engagement" value={s ? fmtDuration(s.avg_engagement_seconds) : '—'} delta={deltaInfo(s?.avg_engagement_seconds, p?.avg_engagement_seconds)} hint="time on page" accent="#16a34a" />
        <MetricCard label="Avg. session" value={s ? fmtDuration(s.avg_session_seconds) : '—'} delta={deltaInfo(s?.avg_session_seconds, p?.avg_session_seconds)} accent="#0891b2" />
        <MetricCard label="Bounce rate" value={s ? fmtPct(s.bounce_rate) : '—'} delta={deltaInfo(s?.bounce_rate, p?.bounce_rate, true)} accent="#ef4444" />
        <MetricCard label="Pages / session" value={s ? (s.pages_per_session || 0).toFixed(1) : '—'} delta={deltaInfo(s?.pages_per_session, p?.pages_per_session)} accent="#5b21b6" />
        <MetricCard label="Events" value={fmt(s?.events)} delta={deltaInfo(s?.events, p?.events)} accent="#16a34a" series={ts} dataKey="events" />
        <MetricCard label="Signups" value={fmt(s?.signups)} hint="new accounts in range" accent="#9d174d" />
      </div>

      <div style={{ ...CARD, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div style={SECTION_HEAD}>Traffic over time</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {METRICS.map((mm) => (
              <button key={mm.key} type="button" onClick={() => setMetric(mm.key)} style={{
                padding: '5px 11px', borderRadius: 999, border: `1px solid ${metric === mm.key ? mm.color : '#e5e1f2'}`, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                background: metric === mm.key ? `${mm.color}14` : '#fff', color: metric === mm.key ? mm.color : '#6b7280',
              }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: mm.color, marginRight: 6 }} />
                {mm.label}
              </button>
            ))}
          </div>
        </div>
        <TrafficChart data={ts} metric={metric} interval={data?.interval} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 16 }}>
        <BreakdownCard title="Conversion funnel" note="session → engaged → converted">
          <Funnel steps={data?.funnel} />
          <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 10 }}>
            Conversions counted: {(data?.conversionEvents || []).join(', ') || '—'}
          </div>
        </BreakdownCard>
        <BreakdownCard title="Top events" note="★ = conversion">
          <BarList rows={data?.events} primaryKey="count" secondaryKey="visitors" primaryLabel="Count" secondaryLabel="Visitors" accent="#16a34a" empty="No custom events yet." />
        </BreakdownCard>
      </div>

      <div style={{ ...CARD, marginBottom: 16 }}>
        <div style={SECTION_HEAD}>Top pages</div>
        <BarList rows={data?.breakdowns?.pages} primaryKey="pageviews" secondaryKey="visitors" primaryLabel="Views" secondaryLabel="Visitors"
          extra={{ key: 'avg_seconds', label: 'Avg time', format: (v) => (v == null ? '—' : fmtDuration(v)) }}
          renderKey={(k) => k || '/'} filterKey="path" onFilter={applyFilter} activeValue={filters.path} empty="No pageviews yet." />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginBottom: 16 }}>
        <BreakdownCard title="Landing pages" note="session entry"><BarList rows={data?.breakdowns?.landing} primaryKey="sessions" secondaryKey={null} primaryLabel="Sessions" renderKey={(k) => k || '/'} accent="#0891b2" filterKey="path" onFilter={applyFilter} activeValue={filters.path} /></BreakdownCard>
        <BreakdownCard title="Exit pages" note="session exit"><BarList rows={data?.breakdowns?.exit} primaryKey="sessions" secondaryKey={null} primaryLabel="Sessions" renderKey={(k) => k || '/'} accent="#db2777" filterKey="path" onFilter={applyFilter} activeValue={filters.path} /></BreakdownCard>
        <BreakdownCard title="Channels"><BarList rows={data?.breakdowns?.channels} accent="#7c3aed" filterKey="channel" onFilter={applyFilter} activeValue={filters.channel} /></BreakdownCard>
        <BreakdownCard title="Sources"><BarList rows={data?.breakdowns?.sources} accent="#0891b2" filterKey="source" onFilter={applyFilter} activeValue={filters.source} /></BreakdownCard>
        <BreakdownCard title="Referrers"><BarList rows={data?.breakdowns?.referrers} accent="#db2777" empty="No external referrers yet." filterKey="referrer" onFilter={applyFilter} activeValue={filters.referrer} /></BreakdownCard>
        <BreakdownCard title="Devices"><BarList rows={data?.breakdowns?.devices} accent="#16a34a" renderKey={(k) => (k ? k[0].toUpperCase() + k.slice(1) : '(not set)')} filterKey="device" onFilter={applyFilter} activeValue={filters.device} /></BreakdownCard>
        <BreakdownCard title="Browsers"><BarList rows={data?.breakdowns?.browsers} accent="#ea580c" filterKey="browser" onFilter={applyFilter} activeValue={filters.browser} /></BreakdownCard>
        <BreakdownCard title="Operating systems"><BarList rows={data?.breakdowns?.os} accent="#5b21b6" filterKey="os" onFilter={applyFilter} activeValue={filters.os} /></BreakdownCard>
        <BreakdownCard title="Countries (approx.)"><BarList rows={data?.breakdowns?.countries} accent="#0891b2" renderKey={(k) => <span>{flagEmoji(k)} {k}</span>} empty="No geography yet." filterKey="country" onFilter={applyFilter} activeValue={filters.country} /></BreakdownCard>
        <BreakdownCard title="Languages"><BarList rows={data?.breakdowns?.languages} accent="#9d174d" filterKey="language" onFilter={applyFilter} activeValue={filters.language} /></BreakdownCard>
      </div>

      <div style={{ ...CARD, marginBottom: 16 }}>
        <div style={SECTION_HEAD}>Traffic heatmap</div>
        <Heatmap cells={insights?.heatmap} />
      </div>

      <div style={{ ...CARD, marginBottom: 16 }}>
        <div style={SECTION_HEAD}>Weekly retention cohorts</div>
        <Cohorts rows={insights?.cohorts} />
      </div>
    </div>
  );
}
